import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '../context/API';

const ADMIN_EARNINGS = '/admin/merchant-earnings';
const ADMIN_EARNINGS_EXPORT = '/admin/merchant-earnings/export';

export interface MerchantEarningsRow {
  merchantId: string;
  merchantName: string;
  email: string;
  totalRevenue: number;
  totalFees: number;
  netEarnings: number;
  orderCount: number;
  averageOrderValue: number;
}

export interface EarningsPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface EarningsTotals {
  totalRevenue: number;
  totalFees: number;
  netEarnings: number;
  orderCount: number;
}

export interface SummaryStats {
  totalRevenue: number;
  totalFees: number;
  totalNetEarnings: number;
  totalOrders: number;
  merchantCount: number;
  averageOrderValue: number;
}

export interface PlatformBreakdownItem {
  orderCount?: number;
  revenue?: number;
  fees?: number;
  netEarnings?: number;
}

export interface MerchantEarningsDetails {
  summary?: {
    totalRevenue?: number;
    totalFees?: number;
    netEarnings?: number;
    orderCount?: number;
    averageOrderValue?: number;
  };
  platformBreakdown?: {
    kiosk?: PlatformBreakdownItem;
    mobile?: PlatformBreakdownItem;
  };
}

interface AdminEarningsApiResponse {
  message?: string;
  status?: number;
  success?: boolean;
  data?: {
    data?: MerchantEarningsRow[];
    pagination?: EarningsPagination;
    totals?: EarningsTotals;
  };
}

interface MerchantEarningsState {
  rows: MerchantEarningsRow[];
  pagination: EarningsPagination | null;
  summaryStats: SummaryStats | null;
  dateRange: { startDate: string; endDate: string };
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  searchTerm: string;
  loading: boolean;
  error: string | null;
  selectedMerchant: MerchantEarningsRow | null;
  merchantDetails: MerchantEarningsDetails | null;
  detailsLoading: boolean;
}

const defaultDateRange = (): { startDate: string; endDate: string } => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

const initialState: MerchantEarningsState = {
  rows: [],
  pagination: null,
  summaryStats: null,
  dateRange: defaultDateRange(),
  page: 1,
  limit: 20,
  sortBy: 'netEarnings',
  sortOrder: 'desc',
  searchTerm: '',
  loading: false,
  error: null,
  selectedMerchant: null,
  merchantDetails: null,
  detailsLoading: false,
};

export const fetchAdminMerchantEarnings = createAsyncThunk<
  {
    rows: MerchantEarningsRow[];
    pagination: EarningsPagination | null;
    summaryStats: SummaryStats | null;
  },
  void,
  { state: { merchantEarningsOverview: MerchantEarningsState }; rejectValue: string }
>(
  'merchantEarnings/fetchList',
  async (_, { getState, rejectWithValue }) => {
    const s = getState().merchantEarningsOverview;
    const params = {
      startDate: s.dateRange.startDate,
      endDate: s.dateRange.endDate,
      page: s.page,
      limit: s.limit,
      sortBy: s.sortBy,
      sortOrder: s.sortOrder,
      _t: Date.now(),
    };
    try {
      const response = await API.get<AdminEarningsApiResponse>(ADMIN_EARNINGS, {
        params,
      });
      const data = response.data?.data;
      const list = data?.data ?? [];
      const pag = data?.pagination ?? null;
      const totals = data?.totals;
      const summaryStats: SummaryStats | null = totals
        ? {
            totalRevenue: totals.totalRevenue ?? 0,
            totalFees: totals.totalFees ?? 0,
            totalNetEarnings: totals.netEarnings ?? 0,
            totalOrders: totals.orderCount ?? 0,
            merchantCount: pag?.totalItems ?? list.length,
            averageOrderValue:
              (totals.orderCount ?? 0) > 0
                ? (totals.totalRevenue ?? 0) / (totals.orderCount ?? 0)
                : 0,
          }
        : null;
      return { rows: list, pagination: pag, summaryStats };
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data
              ?.message
          : null;
      return rejectWithValue(
        typeof message === 'string' ? message : 'Failed to fetch merchant earnings'
      );
    }
  }
);

export const fetchMerchantEarningsDetails = createAsyncThunk<
  MerchantEarningsDetails | null,
  { merchantId: string },
  { state: { merchantEarningsOverview: MerchantEarningsState }; rejectValue: string }
>(
  'merchantEarnings/fetchDetails',
  async ({ merchantId }, { getState, rejectWithValue }) => {
    const { dateRange } = getState().merchantEarningsOverview;
    try {
      const response = await API.get<{ data?: MerchantEarningsDetails }>(
        `/merchant/${merchantId}/earnings`,
        {
          params: {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          },
        }
      );
      return response.data?.data ?? null;
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data
              ?.message
          : null;
      return rejectWithValue(
        typeof message === 'string' ? message : 'Failed to load merchant details'
      );
    }
  }
);

export const exportAdminMerchantEarningsCSV = createAsyncThunk<
  string,
  void,
  { state: { merchantEarningsOverview: MerchantEarningsState }; rejectValue: string }
>(
  'merchantEarnings/exportCSV',
  async (_, { getState, rejectWithValue }) => {
    const s = getState().merchantEarningsOverview;
    try {
      const response = await API.get(ADMIN_EARNINGS_EXPORT, {
        params: {
          startDate: s.dateRange.startDate,
          endDate: s.dateRange.endDate,
          sortBy: s.sortBy,
          sortOrder: s.sortOrder,
        },
        responseType: 'text',
      });
      return typeof response.data === 'string' ? response.data : '';
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data
              ?.message
          : null;
      return rejectWithValue(
        typeof message === 'string' ? message : 'Failed to export CSV'
      );
    }
  }
);

const merchantEarningsSlice = createSlice({
  name: 'merchantEarnings',
  initialState,
  reducers: {
    setDateRange: (
      state,
      action: PayloadAction<{ startDate: string; endDate: string }>
    ) => {
      state.dateRange = action.payload;
      state.page = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
      state.page = 1;
    },
    setSort: (
      state,
      action: PayloadAction<{ sortBy: string; sortOrder: 'asc' | 'desc' }>
    ) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
      state.page = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setSelectedMerchant: (
      state,
      action: PayloadAction<MerchantEarningsRow | null>
    ) => {
      state.selectedMerchant = action.payload;
      if (!action.payload) {
        state.merchantDetails = null;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminMerchantEarnings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminMerchantEarnings.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = action.payload.rows;
        state.pagination = action.payload.pagination;
        state.summaryStats = action.payload.summaryStats;
        state.error = null;
      })
      .addCase(fetchAdminMerchantEarnings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to fetch';
      })
      .addCase(fetchMerchantEarningsDetails.pending, (state) => {
        state.detailsLoading = true;
      })
      .addCase(fetchMerchantEarningsDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.merchantDetails = action.payload;
      })
      .addCase(fetchMerchantEarningsDetails.rejected, (state) => {
        state.detailsLoading = false;
      })
      .addCase(exportAdminMerchantEarningsCSV.fulfilled, () => {})
      .addCase(exportAdminMerchantEarningsCSV.rejected, () => {});
  },
});

export const {
  setDateRange,
  setPage,
  setLimit,
  setSort,
  setSearchTerm,
  setSelectedMerchant,
  clearError,
} = merchantEarningsSlice.actions;
export default merchantEarningsSlice.reducer;
